import React from 'react';

import {
  AutocompleteUniteFOResultDtoFieldEnum,
  UniteFoViolationDtoFormFieldEnum,
} from 'lib_api/lib/api/gen';

import AutocompleteAdresse from 'components/BaseForm/Autocomplete/AutocompleteAdresse';
import BaseInputCodeInsee from 'components/BaseForm/BaseInputCodeInsee';
import HelpMessage from 'components/BaseForm/HelpMessage';
import BaseSelect from 'components/BaseForm/Select/BaseSelect';
import BaseSelectWithSearch from 'components/BaseForm/Select/BaseSelectWithSearch';
import { useApi } from 'hooks/ApiStoreContext';
import { useSubmitReferentielForm } from 'hooks/referentiels/useSubmitReferentielForm';
import { useValidateReferentielForm } from 'hooks/referentiels/useValidateReferentielForm';
import { useAsyncSearchUniteFo } from 'hooks/search/async/useAsyncSearchUniteFo';
import { UniteFoFormValues } from 'types/referentiels/UniteFo';

import ModalEdition from '../../Modals/ModalEdition/ModalEdition';
import { buildRequestDto, getTypeUniteMessage } from '../utils';

interface ModalCreateProps {
  close: () => void;
  visible: boolean;
  refreshDashboard: () => void;
}

export default function ModalCreate({
  close,
  visible,
  refreshDashboard,
}: ModalCreateProps): React.ReactElement {
  const { AdminFonctionnelControllerApi } = useApi();
  const { submit, inProgress } = useSubmitReferentielForm(
    refreshDashboard,
    close,
    (values: UniteFoFormValues) =>
      AdminFonctionnelControllerApi.createUniteFoUsingPOST(
        buildRequestDto(values),
      ),
  );
  const { validate } = useValidateReferentielForm(
    (field: UniteFoViolationDtoFormFieldEnum, values: UniteFoFormValues) =>
      AdminFonctionnelControllerApi.validateCreateUniteFoFieldUsingPOST(
        field,
        buildRequestDto(values),
      ),
    response => response.uniteFoViolationsDto ?? [],
  );

  return (
    <ModalEdition
      close={close}
      visible={visible}
      title="Créer unité force de l'ordre"
      initials={{}}
      isSubmitting={inProgress}
      submit={submit}
      validate={validate}
      inputs={[
        {
          field: UniteFoViolationDtoFormFieldEnum.TYPE,
          name: 'type',
          label: "Type d'unité",
          required: true,
          renderAfterInput: (values: UniteFoFormValues) => (
            <HelpMessage
              message={getTypeUniteMessage(values.type)}
              showMessage={values.type !== undefined}
            />
          ),
          render: () => {
            return (
              <BaseSelect
                options={[
                  {
                    value: 'GN',
                    displayValue: 'GN',
                  },
                  {
                    value: 'PN',
                    displayValue: 'PN',
                  },
                  {
                    value: 'PP',
                    displayValue: 'PP',
                  },
                  {
                    value: 'PM',
                    displayValue: 'PM',
                  },
                ]}
                getOptionValue={option => option.value}
                getOptionLabel={option => option.displayValue}
              />
            );
          },
        },
        {
          field: UniteFoViolationDtoFormFieldEnum.CODE,
          name: 'code',
          label: 'Code',
          required: true,
        },
        {
          field: UniteFoViolationDtoFormFieldEnum.ABREVIATION,
          name: 'abreviation',
          label: "Abréviation de l'unité",
          required: true,
        },
        {
          field: UniteFoViolationDtoFormFieldEnum.UNITE,
          name: 'unite',
          label: "Libellé de l'unité",
          required: true,
        },
        {
          field: UniteFoViolationDtoFormFieldEnum.PARENT,
          name: 'uniteFoParent',
          label: 'Unité parent',
          render: () => {
            return (
              <BaseSelectWithSearch
                useSearchEntities={useAsyncSearchUniteFo(
                  AutocompleteUniteFOResultDtoFieldEnum.ABREVIATION,
                )}
                getOptionValue={option => option.idCorrelation}
                getOptionLabel={option => option.abreviation}
                allowClear
              />
            );
          },
        },
        {
          name: 'adresse',
          label: "Adresse de l'unité",
          render: ({ name }: { name: string }) => {
            return (
              <AutocompleteAdresse
                name={name}
                communeField="ville"
                codePostalField="codePostal"
              />
            );
          },
        },
        {
          field: UniteFoViolationDtoFormFieldEnum.CODE_POSTAL,
          name: 'codePostal',
          label: 'Code postal',
        },
        {
          name: 'ville',
          label: 'Ville',
          normalize: (value: string) => value.toUpperCase(),
        },
        {
          field: UniteFoViolationDtoFormFieldEnum.CODE_INSEE,
          name: 'codeInsee',
          label: 'Code INSEE',
          render: ({ name }: { name: string }) => {
            return (
              <BaseInputCodeInsee name={name} communeField="ctRattachement" />
            );
          },
        },
        {
          name: 'ctRattachement',
          label: 'CT de rattachement',
        },
        {
          field: UniteFoViolationDtoFormFieldEnum.EMAIL,
          name: 'email',
          label: 'Courriel',
        },
        {
          field: UniteFoViolationDtoFormFieldEnum.TELEPHONE,
          name: 'telephone',
          label: 'Téléphone',
        },
      ]}
    />
  );
}
